import React from "react";
import { parsePoint } from "./helpers";
import { ElasticsearchDocument } from "./types/Elasticsearch";
import {
  MapContainer,
  Marker,
  Popup,
  TileLayer,
  useMapEvents,
} from "react-leaflet";
import L from "leaflet";
import "leaflet/dist/leaflet.css";
import { Box, Typography } from "@mui/material";
import { useMapContext } from "./MapContext";
import { DrawerContentType, useDrawerContext } from "./DrawerContext";

// Define a custom icon
const pinIcon = L.icon({
  iconUrl: "/icons/pin.png", // Replace with the path to your icon image
  iconSize: [32, 32], // Size of the icon [width, height]
  // iconAnchor: [12, 41], // Anchor point of the icon [x, y]
  // popupAnchor: [0, -41], // Anchor point for the popup [x, y]
});

interface MapViewProps {
  documents: ElasticsearchDocument[];
  onMapClick: (latlng: L.LatLng) => void;
  onMapRightClick: (event: {
    latlng: L.LatLng;
    containerPoint: { x: number; y: number };
  }) => void;
}

const MapView = (props: MapViewProps) => {
  const { setSelectedDocument } = useMapContext();
  const { setDrawerContentType } = useDrawerContext();

  /**
   * Handles map click events: left click and right click.
   * @returns null
   */
  const MapClickHandler = () => {
    useMapEvents({
      click(e) {
        props.onMapClick(e.latlng);
      },
      // This handles right click on map
      contextmenu(e) {
        props.onMapRightClick({
          latlng: e.latlng,
          containerPoint: e.containerPoint,
        });
      },
    });
    return null;
  };

  return (
    <MapContainer
      center={[37.4724, -122.14]}
      zoom={15}
      className="h-full w-full"
      style={{ width: "100%", height: "100%", zIndex: 0 }}
    >
      <TileLayer
        url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
        maxZoom={19} // Set the maximum zoom level
      />
      {props.documents?.map((doc: ElasticsearchDocument) => {
        const point = parsePoint(doc._source.location);
        if (!point) return null; // Skip if point is invalid

        const thumbnailUrl = `https://image.mux.com/${
          doc._source.playbackId
        }/thumbnail.jpg?width=214&height=121&fit_mode=smartcrop${
          doc._source.secondsIntoVideo
            ? `&time=${doc._source.secondsIntoVideo}`
            : ""
        }`;

        return (
          <Marker
            key={doc._id}
            position={[point.lat, point.lon]}
            icon={pinIcon}
          >
            <Popup>
              <Typography
                variant="body2"
                style={{ marginTop: 4, marginBottom: 4 }}
              >
                {doc._source.category}
              </Typography>
              <Box>{doc._source.description || "No description available"}</Box>
              {/* Thumbnail from live stream, if one exists */}
              {doc._source.playbackId && (
                <img
                  src={thumbnailUrl}
                  alt="Thumbnail"
                  style={{ width: "100%", height: "auto", marginTop: 8 }}
                />
              )}
              <a
                href="#"
                onClick={(e) => {
                  e.preventDefault(); // Prevent the default anchor behavior
                  setSelectedDocument({ id: doc._id });
                  setDrawerContentType(DrawerContentType.VIEW_DETAILS);
                }}
                style={{
                  color: "blue",
                  textDecoration: "underline",
                  cursor: "pointer",
                }}
              >
                View details
              </a>
            </Popup>
          </Marker>
        );
      })}
      <MapClickHandler />
    </MapContainer>
  );
};

export default MapView;
